/* eslint-disable react/require-default-props */
import { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import dayjs from "@shared/dayjs";

import Modal from "@shared/v2/Modal";
import Button from "@shared/v2/Button/Button";
import Dropdown from "@shared/v2/Dropdown";
import FieldLabel from "@shared/v2/FieldLabel";
import DatePicker from "@shared/v2/DatePicker";
import ErrorBanner from "@shared/v2/ErrorBanner/ErrorBanner";
import Checkbox from "@shared/v2/Checkbox";
import TextInput from "@shared/v2/TextInput";
import { PencilSolidV6 } from "@shared/v2/Icomoon";
import { responseDataToCamelCase } from "@shared/v2/caseTransformingAxios";
import ConnectServicesClientSelector from "../../../shared/ConnectServicesClientSelector";
import ListingPreviewCard from "../../../../Transactions/ListingPreviewCard";
import AddEditModalBody from "../../../../shared/AddressModal/AddEditModalBody";
import format from "../../../../shared/formatters";
import apiClient from "../api/apiClient";
import { useAccountProducts } from "../../../../reducers/layoutReducer/selectors";

const TRANSACTION_STATUS_OPTIONS = [{ value: "pending", label: "Pending" }];
const SECTION_HEADER_CLASS = "tw-text-gray-base tw-text-18d tw-font-bold tw-mb-[-8px]";

const UpdateTransactionModal = ({
  isUpdateTransactionModalOpen,
  updateTransactionMlsData,
  propertyDetails,
  listingDetailsData,
  transactionDetails,
  accountSources,
  accountPropertyTypes,
  mutualAcceptanceDateMilestone,
  closeDateMilestone,
  uuid,
  putDetailAsThunk,
  updateDateAsThunk,
  closeModalAsThunk,
  closeUpdateTransactionModal,
  clearUpdateTransactionMlsData,
  setClosedPrice,
  fetchFinancials,
  preStatus,
  closePriceVal,
  headerClosePriceVal,
  revertStatus,
  stopStatusLoading,
  clients,
  transactionType,
  connectedService,
  setConnectedService,
  setConnectServicesError,
  clientRoles,
  consolidateDates,
  refreshPeople,
}) => {
  const accountProducts = useAccountProducts();

  const getInitialFormData = () => {
    const mlsData = updateTransactionMlsData;
    const importPayload = mlsData?.importPayload || {};
    const importAddress = importPayload.address_attributes || {};
    const importDetails = importPayload.details || [];
    const findDetail = (name) => importDetails.find((d) => d.name === name)?.value;
    const firstDefined = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "");

    return {
      transactionName: transactionDetails?.title || "",
      status: "pending",
      source: listingDetailsData?.source?.id || "",
      mutualAcceptanceDate: mutualAcceptanceDateMilestone?.date || "",
      closeDate: closeDateMilestone?.date || "",
      useCustomTitle: transactionDetails?.use_custom_title ?? true,
      propertyType: importPayload.property_type_id || propertyDetails?.property_type?.id || "",
      mlsNumber:
        firstDefined(
          importPayload.mls_number,
          mlsData?.mlsNum,
          mlsData?.mlsNumber,
          listingDetailsData?.mls_number,
        ) || "",
      streetAddress:
        firstDefined(importAddress.street_address, mlsData?.streetAddress, propertyDetails?.street_address) ||
        "",
      city: firstDefined(importAddress.city, mlsData?.city, propertyDetails?.city) || "",
      locality: firstDefined(importAddress.locality, mlsData?.state, propertyDetails?.state) || "",
      postalCode: firstDefined(importAddress.postal_code, mlsData?.postalCode, propertyDetails?.zip) || "",
      country: importAddress.country || propertyDetails?.country || "US",
      price: firstDefined(importPayload.price, mlsData?.price, listingDetailsData?.price) || "",
      estClosePrice: closePriceVal || headerClosePriceVal || "",
      beds: findDetail("beds") ?? mlsData?.bedrooms ?? mlsData?.beds ?? propertyDetails?.beds ?? "",
      baths:
        findDetail("baths") ?? mlsData?.bathsTotalDecimal ?? mlsData?.baths ?? propertyDetails?.baths ?? "",
      sqft:
        findDetail("sqft") ??
        mlsData?.squareFeet ??
        mlsData?.sqFt ??
        mlsData?.sqft ??
        propertyDetails?.sqft ??
        "",
      description:
        firstDefined(
          importPayload.description,
          mlsData?.remarks,
          mlsData?.description,
          listingDetailsData?.description,
        ) || "",
      connectServicesClient: null,
      connectServicesDisableReason: null,
    };
  };
  const [formData, setFormData] = useState(getInitialFormData);
  const isBuyerTenantType = transactionType === "buyer" || transactionType === "tenant";
  const showConnectServices = accountProducts.connectService && isBuyerTenantType;

  const [syncWithAddress, setSyncWithAddress] = useState(!(transactionDetails?.use_custom_title ?? true));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedPriceField, setFocusedPriceField] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [connectServiceBypass, setConnectServiceBypass] = useState(false);

  useEffect(() => {
    // When the Connect Services section is not shown, nothing can report its validity, so we
    // consider it valid/complete by default.
    setConnectServiceBypass(!showConnectServices);
  }, [showConnectServices]);

  const clientOptions = useMemo(
    () =>
      (clients || []).map((c) => ({
        label: `${c.person.name} ${c.person.email_address}`,
        value: c.id,
        roleId: c.role?.id,
        name: c.person.name,
        email: c.person.email_address,
      })),
    [clients],
  );

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
    setShowValidationBanner(false);
  };

  const handleCurrencyChange = (field) => (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    const parts = raw.split(".");
    if (parts.length > 2) return;
    if (parts[1]?.length > 2) return;
    if (raw === "" || !Number.isNaN(Number(raw))) {
      updateField(field, raw);
    }
  };

  const getCurrencyDisplayValue = (field, value) =>
    focusedPriceField === field ? value : format.currency(Number(value) || 0, 0);

  useEffect(() => {
    if (isUpdateTransactionModalOpen) {
      setSyncWithAddress(!(transactionDetails?.use_custom_title ?? true));
      setFormData(() => {
        const initial = getInitialFormData();
        if (clientOptions.length > 0 && clientRoles?.length > 0) {
          const firstClient = clientOptions[0];
          const role = clientRoles.find((r) => r.id === firstClient.roleId);
          if (firstClient && role) {
            initial.connectServicesClient = {
              label: `${role.title} (${firstClient.name} ${firstClient.email})`,
              value: firstClient.value,
            };
          }
        }
        return initial;
      });
      setErrors({});
      setValidationErrors([]);
      setShowValidationBanner(false);
    }
  }, [
    isUpdateTransactionModalOpen,
    transactionDetails,
    propertyDetails,
    listingDetailsData,
    updateTransactionMlsData,
    mutualAcceptanceDateMilestone,
    closeDateMilestone,
    closePriceVal,
    headerClosePriceVal,
    clientOptions,
    clientRoles,
  ]);

  const daysUntilClose = useMemo(() => {
    if (!formData.closeDate) return null;

    let formattedCloseDate = formData.closeDate;

    if (dayjs(formData.closeDate, "YYYY-MM-DD", true).isValid()) {
      formattedCloseDate = dayjs(formData.closeDate, "YYYY-MM-DD").format("MM/DD/YYYY");
    }

    const closeDate = dayjs(formattedCloseDate, "MM/DD/YYYY", true);

    if (!closeDate.isValid()) return null;

    return closeDate.diff(dayjs().startOf("day"), "days");
  }, [formData.closeDate]);

  const isCloseDateInPast = daysUntilClose !== null && daysUntilClose < 0;

  useEffect(() => {
    if (isCloseDateInPast) {
      setFormData((prev) => ({
        ...prev,
        connectServicesClient: null,
        connectServicesDisableReason: null,
      }));
    }
  }, [isCloseDateInPast]);

  const hasMlsData = Boolean(updateTransactionMlsData);

  const currentStreetAddress = useMemo(() => {
    if (hasMlsData) {
      return updateTransactionMlsData?.streetAddress || "";
    }
    return formData.streetAddress;
  }, [hasMlsData, updateTransactionMlsData, formData.streetAddress]);

  useEffect(() => {
    if (syncWithAddress && currentStreetAddress) {
      setFormData((prev) => {
        if (prev.useCustomTitle) return prev;
        return {
          ...prev,
          transactionName: currentStreetAddress,
          useCustomTitle: false,
        };
      });
    }
  }, [syncWithAddress, currentStreetAddress]);

  const [addressError, setAddressError] = useState(null);

  const updateCompleteAddress = ({ streetAddress, city, locality, postalCode, country }) => {
    setFormData((prev) => ({
      ...prev,
      streetAddress: streetAddress || prev.streetAddress,
      city: city || prev.city,
      locality: locality || prev.locality,
      postalCode: postalCode || prev.postalCode,
      country: country || prev.country,
    }));
  };

  const statusValue = TRANSACTION_STATUS_OPTIONS.find((o) => o.value === formData.status);
  const sourceValue = accountSources.find(
    (o) => String(o.value) === String(formData.source) || o.label === formData.source,
  );
  const propertyTypeValue = accountPropertyTypes.find((o) => o.value === formData.propertyType);

  const handleSyncWithAddressChange = (checked) => {
    setSyncWithAddress(checked);
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        transactionName: currentStreetAddress,
        useCustomTitle: false,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        useCustomTitle: true,
      }));
    }
  };

  const validateForm = () => {
    const fieldErrors = {};
    const bannerErrors = [];

    if (!formData.source) {
      fieldErrors.source = "Source is required";
      bannerErrors.push({ render: "Source is a required field" });
    }

    if (!formData.closeDate) {
      fieldErrors.closeDate = "Close date is required";
      bannerErrors.push({ render: "Close Date is a required field" });
    }

    if (
      !formData.streetAddress ||
      !formData.city ||
      !formData.locality ||
      !formData.postalCode ||
      !formData.country
    ) {
      bannerErrors.push({
        render: "Street Address, City, State/Province, Country, and Zip Code are a required field",
      });
    }

    if (!connectServiceBypass) {
      bannerErrors.push({ render: "Connect client contact is a required field" });
    }

    setErrors(fieldErrors);
    setValidationErrors(bannerErrors);
    setShowValidationBanner(bannerErrors.length > 0);
    return bannerErrors.length === 0;
  };

  const handleCancel = () => {
    if (preStatus) {
      revertStatus(uuid, preStatus);
    }
    closeUpdateTransactionModal();
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const currentUseCustomTitle = transactionDetails?.use_custom_title ?? true;
      const newUseCustomTitle = !syncWithAddress;
      const addressId = propertyDetails?.id;
      const currentSourceId = listingDetailsData?.source?.id;

      const importPayload = updateTransactionMlsData?.importPayload || {};
      const listingPayload = { ...importPayload };

      if (newUseCustomTitle !== currentUseCustomTitle) {
        listingPayload.use_custom_title = newUseCustomTitle;
      }
      if (formData.transactionName !== transactionDetails?.title) {
        listingPayload.title = formData.transactionName;
      }
      listingPayload.status = formData.status;
      if (formData.estClosePrice !== transactionDetails?.est_close_price) {
        listingPayload.est_close_price = formData.estClosePrice;
      }

      if (formData.source !== currentSourceId) {
        listingPayload.source_id = formData.source;
      }

      if (!hasMlsData) {
        if (formData.price !== listingDetailsData?.price) {
          listingPayload.price = formData.price;
        }
        if (formData.mlsNumber !== listingDetailsData?.mls_number) {
          listingPayload.mls_number = formData.mlsNumber;
        }
        if (formData.description !== listingDetailsData?.description) {
          listingPayload.description = formData.description;
        }

        if (formData.propertyType !== propertyDetails?.property_type?.id) {
          listingPayload.property_type_id = formData.propertyType;
        }

        const addressChanges = {};
        if (formData.streetAddress !== propertyDetails?.street_address) {
          addressChanges.street_address = formData.streetAddress;
        }
        if (formData.city !== propertyDetails?.city) {
          addressChanges.city = formData.city;
        }
        if (formData.locality !== propertyDetails?.state) {
          addressChanges.locality = formData.locality;
        }
        if (formData.postalCode !== propertyDetails?.zip) {
          addressChanges.postal_code = formData.postalCode;
        }
        if (formData.country !== propertyDetails?.country) {
          addressChanges.country = formData.country;
        }
        if (Object.keys(addressChanges).length > 0) {
          listingPayload.address_attributes = {
            ...addressChanges,
            ...(addressId ? { id: addressId } : {}),
          };
        }

        const detailsArray = [];
        if (formData.beds !== propertyDetails?.beds) {
          detailsArray.push({ name: "beds", value: formData.beds });
        }
        if (formData.baths !== propertyDetails?.baths) {
          detailsArray.push({ name: "baths", value: formData.baths });
        }
        if (formData.sqft !== propertyDetails?.sqft) {
          detailsArray.push({ name: "sqft", value: formData.sqft });
        }
        if (detailsArray.length > 0) {
          listingPayload.details = detailsArray;
        }
      }

      if (consolidateDates) {
        if (formData.mutualAcceptanceDate) {
          const newDate = dayjs.utc(formData.mutualAcceptanceDate, "MM/DD/YYYY");
          if (newDate.isValid()) {
            listingPayload.mutual_acceptance_date = newDate.format("YYYY-MM-DD");
          }
        }
        if (formData.closeDate) {
          const newCloseDate = dayjs.utc(formData.closeDate, "MM/DD/YYYY");
          if (newCloseDate.isValid()) {
            listingPayload.close_date = newCloseDate.format("YYYY-MM-DD");
          }
        }
      }

      if (Object.keys(listingPayload).length > 0) {
        await putDetailAsThunk({
          uuid,
          payload: listingPayload,
          group: "batch",
        });

        if (listingPayload.est_close_price !== undefined) {
          setClosedPrice(formData.estClosePrice);
        }

        await fetchFinancials();
      }

      if (!consolidateDates) {
        const datePromises = [];
        if (formData.mutualAcceptanceDate && mutualAcceptanceDateMilestone?.id) {
          const newDate = dayjs.utc(formData.mutualAcceptanceDate, "MM/DD/YYYY");
          if (newDate.isValid()) {
            datePromises.push(
              updateDateAsThunk(uuid, mutualAcceptanceDateMilestone.id, {
                new_milestone: false,
                date: newDate,
                seconds_since_midnight: null,
              }),
            );
          }
        }
        if (formData.closeDate && closeDateMilestone?.id) {
          const newCloseDate = dayjs.utc(formData.closeDate, "MM/DD/YYYY");
          if (newCloseDate.isValid()) {
            datePromises.push(
              updateDateAsThunk(uuid, closeDateMilestone.id, {
                new_milestone: false,
                date: newCloseDate,
                seconds_since_midnight: null,
              }),
            );
          }
        }
        if (datePromises.length > 0) {
          await Promise.all(datePromises);
        }
      }

      // 502 means the record was created but the synchronous referral attempt failed; the
      // controller still returns the record in the response body. caseTransformingAxios only
      // camelCases successful responses, so we apply the transform here too.
      const createConnectedServiceHandlingFailure = async (...args) => {
        try {
          const response = await apiClient.createConnectedService(...args);
          setConnectedService(response.data);
          setConnectServicesError(false);
        } catch (err) {
          if (err?.response?.status === 502 && err.response.data) {
            setConnectedService(responseDataToCamelCase(err.response.data));
            setConnectServicesError(true);
            return;
          }
          throw err;
        }
      };

      if (isCloseDateInPast && showConnectServices) {
        if (!connectedService) {
          await createConnectedServiceHandlingFailure(uuid);
        }
      } else if (formData?.connectServicesDisableReason && showConnectServices) {
        if (connectedService) {
          const response = await apiClient.updateConnectedService(uuid, {
            disabled_reason: formData.connectServicesDisableReason.value,
          });
          setConnectedService(response.data);
        } else {
          await createConnectedServiceHandlingFailure(uuid, {
            disabledReason: formData.connectServicesDisableReason.value,
          });
        }
      } else if (formData?.connectServicesClient && showConnectServices) {
        const client = formData.connectServicesClient;
        const isNewClient = !client.value && client.role?.value && client.client?.value;
        const clientPayload = client.value
          ? { listingPersonId: client.value }
          : { roleId: client.role?.value, personId: client.client?.value };
        if (connectedService) {
          const response = await apiClient.updateConnectedService(uuid, { enabled: true, ...clientPayload });
          setConnectedService(response.data);
          if (isNewClient && refreshPeople) {
            refreshPeople();
          }
        } else {
          await createConnectedServiceHandlingFailure(uuid, clientPayload);
          if (isNewClient && refreshPeople) {
            refreshPeople();
          }
        }
      }

      stopStatusLoading();
      closeModalAsThunk(preStatus);
      closeUpdateTransactionModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      className="tw-flex tw-justify-center tw-items-center"
      dialogClassName="tw-w-[636px] tw-max-h-[90vh]"
      contentClassName="tw-w-full tw-flex tw-flex-col tw-max-h-[90vh] tw-max-w-[650px]"
      backdrop="static"
      show={isUpdateTransactionModalOpen}
      onHide={handleCancel}
      data-cy="update-transaction-modal"
    >
      <Modal.Header
        titleClassName="tw-mb-12px tw-text-neutral-gray-75"
        title="Update Transaction"
        closeButton
      />

      {showValidationBanner && validationErrors.length > 0 && (
        <div className="tw-mb-16px" data-cy="validation-banner-container">
          <ErrorBanner errors={validationErrors} />
        </div>
      )}

      <Modal.Body className="tw-flex tw-flex-col tw-gap-24px tw-flex-1 tw-overflow-y-auto">
        <div className={SECTION_HEADER_CLASS}>Transaction Detail</div>
        <div className="tw-flex tw-flex-col tw-gap-16px">
          <div className="tw-flex tw-flex-col tw-gap-8px">
            <TextInput
              label="Transaction Name"
              value={formData.transactionName}
              onChange={(e) => updateField("transactionName", e.target.value)}
              placeholder="Enter transaction name"
              disabled={syncWithAddress}
              data-cy="transaction-name-input"
            />
            <Checkbox
              checked={syncWithAddress}
              onChange={(e) => handleSyncWithAddressChange(e.target.checked)}
              label="Sync with address"
              data-cy="sync-with-address-checkbox"
            />
          </div>
          <div className="tw-flex tw-gap-16px">
            <Dropdown
              containerClassName="tw-flex-1"
              label="Transaction Status"
              options={TRANSACTION_STATUS_OPTIONS}
              onChange={(option) => updateField("status", option?.value)}
              value={statusValue}
              placeholder="Select"
              isDisabled
              data-cy="transaction-status-dropdown"
            />
            <Dropdown
              containerClassName="tw-flex-1"
              label="Source"
              options={accountSources}
              onChange={(option) => updateField("source", option?.value)}
              value={sourceValue}
              placeholder="Select"
              isSearchable
              isRequired
              error={errors.source}
              data-cy="source-dropdown"
            />
          </div>
          <div className="tw-flex tw-gap-16px">
            <div className="tw-flex-1">
              <FieldLabel label="Mutual Acceptance Date" />
              <DatePicker
                value={formData.mutualAcceptanceDate}
                onChange={(value) => updateField("mutualAcceptanceDate", value)}
                position="bottom"
                containerClassName="tw-w-full"
                inputProps={{
                  placeholder: "MM/DD/YYYY",
                }}
                data-cy="mutual-acceptance-date-picker"
              />
            </div>
            <div className="tw-flex-1">
              <FieldLabel label="Close Date" isRequired />
              <DatePicker
                value={formData.closeDate}
                onChange={(value) => updateField("closeDate", value)}
                position="bottom"
                containerClassName="tw-w-full"
                inputProps={{
                  error: errors.closeDate,
                  placeholder: "MM/DD/YYYY",
                }}
                data-cy="close-date-picker"
              />
            </div>
          </div>
        </div>

        <div className={SECTION_HEADER_CLASS}>Listing Details</div>
        {hasMlsData ? (
          <ListingPreviewCard
            containerClassName="tw-w-full tw-border-1px tw-border-solid tw-border-gray-10 tw-rounded-8px tw-shadow-[0_2px_8px_0_rgba(213,213,213,0.50)]"
            listingData={updateTransactionMlsData}
            clearListing={clearUpdateTransactionMlsData}
            BtnIcon={PencilSolidV6}
          />
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-16px">
            <div className="tw-flex tw-gap-16px">
              <Dropdown
                containerClassName="tw-flex-1"
                label="Property Type"
                options={accountPropertyTypes}
                onChange={(option) => updateField("propertyType", option?.value)}
                value={propertyTypeValue}
                placeholder="Select"
                isSearchable
                required
                data-cy="property-type-dropdown"
              />
              <TextInput
                label="MLS #"
                value={formData.mlsNumber}
                onChange={(e) => updateField("mlsNumber", e.target.value)}
                containerClassName="tw-flex-1"
                data-cy="mls-number-input"
              />
            </div>

            <AddEditModalBody
              hideAddressType
              hideSecondaryAddress
              standardized
              addressType=""
              setAddressType={() => {}}
              streetAddress={formData.streetAddress}
              setStreetAddress={(value) => updateField("streetAddress", value)}
              secondaryAddress=""
              setSecondaryAddress={() => {}}
              city={formData.city}
              setCity={(value) => updateField("city", value)}
              locality={formData.locality}
              setLocality={(value) => updateField("locality", value)}
              postalCode={formData.postalCode}
              setPostalCode={(value) => updateField("postalCode", value)}
              country={formData.country}
              setCountry={(value) => updateField("country", value)}
              error={addressError}
              setError={setAddressError}
              updateCompleteAddress={updateCompleteAddress}
              isStreetAddressRequired
              areAddressFieldsRequired
            />

            <TextInput
              label="Listing Price"
              placeholder="0"
              value={getCurrencyDisplayValue("price", formData.price)}
              onChange={handleCurrencyChange("price")}
              onFocus={() => setFocusedPriceField("price")}
              onBlur={() => setFocusedPriceField(null)}
              containerClassName="tw-flex-1"
              data-cy="listing-price-input"
            />

            <div className="tw-flex tw-gap-16px">
              <TextInput
                label="Beds"
                value={formData.beds}
                onChange={(e) => updateField("beds", e.target.value)}
                containerClassName="tw-flex-1"
                data-cy="beds-input"
              />
              <TextInput
                label="Baths"
                value={formData.baths}
                onChange={(e) => updateField("baths", e.target.value)}
                containerClassName="tw-flex-1"
                data-cy="baths-input"
              />
              <TextInput
                label="SqFt"
                value={formData.sqft}
                onChange={(e) => updateField("sqft", e.target.value)}
                containerClassName="tw-flex-1"
                data-cy="sqft-input"
              />
            </div>

            <TextInput
              label="Listing Description"
              value={formData.description}
              onChange={(e) => updateField("description", e.target.value)}
              multiline
              data-cy="listing-description-input"
            />
          </div>
        )}
        <TextInput
          label="Estimated Close Price"
          placeholder="0"
          value={getCurrencyDisplayValue("estClosePrice", formData.estClosePrice)}
          onChange={handleCurrencyChange("estClosePrice")}
          onFocus={() => setFocusedPriceField("estClosePrice")}
          onBlur={() => setFocusedPriceField(null)}
          containerClassName="tw-basis-[calc(50%-4px)] tw-w-[calc(50%-4px)]"
          data-cy="est-close-price-input"
        />
        {showConnectServices && (
          <>
            <div className={SECTION_HEADER_CLASS}>Services</div>

            <ConnectServicesClientSelector
              clientOptions={clientOptions}
              selectedClient={formData.connectServicesClient}
              onClientChange={(option) => updateField("connectServicesClient", option)}
              showToggle
              showSendTodayText={daysUntilClose !== null && daysUntilClose >= 0 && daysUntilClose <= 20}
              showSendFutureText={daysUntilClose >= 21}
              disableReason={formData.connectServicesDisableReason}
              onDisableReasonChange={(option) => updateField("connectServicesDisableReason", option)}
              onValidityChange={setConnectServiceBypass}
              uuid={uuid}
              clientRoles={clientRoles}
              isCloseDateInPast={isCloseDateInPast}
            />
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="tw-flex tw-justify-between tw-gap-8px tw-mt-32px tw-border-l-[0px] tw-border-r-[0px] tw-border-b-[0px] tw-border-t-[1px] tw-border-solid tw-border-gray-10 tw-pt-16px">
        <Button onClick={handleCancel} schema="tertiary" size="medium">
          Cancel
        </Button>
        <Button onClick={handleSubmit} schema="primary" size="medium" isLoading={isSubmitting}>
          Update to Pending
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

UpdateTransactionModal.propTypes = {
  isUpdateTransactionModalOpen: PropTypes.bool,
  closeUpdateTransactionModal: PropTypes.func,
  updateTransactionMlsData: PropTypes.shape({
    beds: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bedrooms: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    city: PropTypes.string,
    baths: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bathsTotalDecimal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mlsNumber: PropTypes.string,
    photos: PropTypes.arrayOf(PropTypes.shape({ url: PropTypes.string })),
    postalCode: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sqFt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    squareFeet: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    state: PropTypes.string,
    streetAddress: PropTypes.string,
    importPayload: PropTypes.shape({}),
  }),
  propertyDetails: PropTypes.shape({
    id: PropTypes.number,
    street_address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    country: PropTypes.string,
    beds: PropTypes.string,
    baths: PropTypes.string,
    sqft: PropTypes.string,
    property_type: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  }),
  listingDetailsData: PropTypes.shape({
    price: PropTypes.string,
    source: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    mls_number: PropTypes.string,
    description: PropTypes.string,
  }),
  accountSources: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  accountPropertyTypes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
    }),
  ),
  mutualAcceptanceDateMilestone: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    date: PropTypes.string,
  }),
  closeDateMilestone: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    date: PropTypes.string,
  }),
  transactionDetails: PropTypes.shape({
    title: PropTypes.string,
    status: PropTypes.string,
    use_custom_title: PropTypes.bool,
    est_close_price: PropTypes.string,
  }),
  uuid: PropTypes.string,
  putDetailAsThunk: PropTypes.func,
  updateDateAsThunk: PropTypes.func,
  closeModalAsThunk: PropTypes.func,
  clearUpdateTransactionMlsData: PropTypes.func,
  setClosedPrice: PropTypes.func,
  fetchFinancials: PropTypes.func,
  preStatus: PropTypes.string,
  closePriceVal: PropTypes.string,
  headerClosePriceVal: PropTypes.string,
  revertStatus: PropTypes.func,
  stopStatusLoading: PropTypes.func,
  clients: PropTypes.arrayOf(PropTypes.shape({})),
  transactionType: PropTypes.string,
  connectedService: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
    sendContactInfoFor: PropTypes.string,
    scheduledSendAt: PropTypes.string,
    referralSentAt: PropTypes.string,
    disabledAt: PropTypes.string,
    disabledReason: PropTypes.string,
    missingInfo: PropTypes.arrayOf(PropTypes.string),
  }),
  setConnectedService: PropTypes.func,
  setConnectServicesError: PropTypes.func,
  clientRoles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  consolidateDates: PropTypes.bool,
  refreshPeople: PropTypes.func,
};

UpdateTransactionModal.defaultProps = {
  isUpdateTransactionModalOpen: false,
  closeUpdateTransactionModal: () => {},
  updateTransactionMlsData: null,
  propertyDetails: null,
  listingDetailsData: null,
  accountSources: [],
  accountPropertyTypes: [],
  mutualAcceptanceDateMilestone: null,
  closeDateMilestone: null,
  transactionDetails: null,
  uuid: null,
  putDetailAsThunk: () => {},
  updateDateAsThunk: () => {},
  closeModalAsThunk: () => {},
  clearUpdateTransactionMlsData: () => {},
  setClosedPrice: () => {},
  fetchFinancials: () => {},
  preStatus: null,
  closePriceVal: null,
  headerClosePriceVal: null,
  revertStatus: () => {},
  stopStatusLoading: () => {},
  clients: [],
  transactionType: null,
  connectedService: null,
  setConnectedService: () => {},
  setConnectServicesError: () => {},
  clientRoles: null,
  consolidateDates: false,
  refreshPeople: null,
};

export default UpdateTransactionModal;
